import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getAiTrafficStats, type LangFilter } from "@/lib/ai-traffic.functions";
import { AdminLangTabs } from "@/components/AdminLangTabs";

export const Route = createFileRoute("/admin/ai-traffic")({
  head: () => ({
    meta: [
      { title: "AI Trafik – Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AiTrafficPage,
});

function fmt(s: string) {
  try {
    return new Date(s).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return s;
  }
}

function AiTrafficPage() {
  const fetcher = useServerFn(getAiTrafficStats);
  const [lang, setLang] = useState<LangFilter>("all");
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["ai-traffic-stats", lang],
    queryFn: () => fetcher({ data: { lang } }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl mb-1">AI Trafik</h1>
          <p className="text-sm text-ink/60">
            Son {data?.windowDays ?? 30} gün: AI botlarının siteyi taraması ve AI asistanlarından gelen ziyaretçiler.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <AdminLangTabs value={lang} onChange={setLang} />
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-full border border-ink/20 px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold hover:border-ink transition-colors disabled:opacity-50"
          >
            {isFetching ? "Yükleniyor…" : "Yenile"}
          </button>
        </div>
      </div>

      {isLoading && <p className="text-ink/60">Yükleniyor…</p>}
      {error && (
        <p className="text-rose-700 text-sm">
          Hata: {error instanceof Error ? error.message : String(error)}
        </p>
      )}

      {data && (
        <div className="space-y-8">
          {/* Summary cards */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-ink/15 p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50">
                Bot tarama isteği
              </div>
              <div className="mt-1 font-serif text-3xl">{data.totalBotHits}</div>
              <div className="text-xs text-ink/60 mt-1">
                Yalnızca sunucu tarafında UA'dan tespit edilen botlar.
              </div>
            </div>
            <div className="rounded-lg border border-ink/15 p-4">
              <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50">
                AI asistanından gelen ziyaret
              </div>
              <div className="mt-1 font-serif text-3xl">{data.totalAiRefs}</div>
              <div className="text-xs text-ink/60 mt-1">
                ChatGPT, Perplexity, Claude, Gemini, Copilot referrer.
              </div>
            </div>
          </div>

          {/* Botlar */}
          <section>
            <h2 className="font-serif text-xl mb-3">Botlara göre</h2>
            {data.byBot.length === 0 ? (
              <p className="text-sm text-ink/60">
                Bu pencerede hiç bot tarama isteği yok. AI botları henüz siteyi ziyaret etmemiş olabilir; kayıt yeni açıldığı için birkaç güne kadar boş görünebilir.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-[0.15em] text-ink/50 border-b border-ink/15">
                    <tr>
                      <th className="text-left py-2">Bot</th>
                      <th className="text-left py-2">Tip</th>
                      <th className="text-right py-2">İstek</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byBot.map((r) => (
                      <tr key={r.bot_name} className="border-b border-ink/5">
                        <td className="py-2 font-mono text-xs">{r.bot_name}</td>
                        <td className="py-2">
                          {r.is_ai ? (
                            <span className="rounded-full bg-moss/15 text-moss px-2 py-0.5 text-[10px] uppercase tracking-wider">
                              AI
                            </span>
                          ) : (
                            <span className="text-ink/50 text-xs">arama</span>
                          )}
                        </td>
                        <td className="py-2 text-right tabular-nums">{r.hits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* AI referrer */}
          <section>
            <h2 className="font-serif text-xl mb-3">AI asistanından gelen ziyaretçi</h2>
            {data.byAiSource.length === 0 ? (
              <p className="text-sm text-ink/60">
                Hiç AI referrer yok. ChatGPT/Perplexity gibi asistanlar sizi kaynak gösterdiğinde ve kullanıcı linke tıkladığında burada görünür. (Claude ve Gemini çoğunlukla referrer göndermez — bot tablosu daha sağlam bir sinyaldir.)
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-[0.15em] text-ink/50 border-b border-ink/15">
                    <tr>
                      <th className="text-left py-2">Kaynak</th>
                      <th className="text-right py-2">Ziyaret</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byAiSource.map((r) => (
                      <tr key={r.ai_source} className="border-b border-ink/5">
                        <td className="py-2 font-mono text-xs">{r.ai_source}</td>
                        <td className="py-2 text-right tabular-nums">{r.visits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Top pages */}
          <div className="grid gap-6 md:grid-cols-2">
            <section>
              <h2 className="font-serif text-xl mb-3">En çok taranan sayfalar</h2>
              {data.topBotPages.length === 0 ? (
                <p className="text-sm text-ink/60">—</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {data.topBotPages.map((r) => (
                    <li key={r.path} className="flex justify-between gap-3">
                      <span className="truncate font-mono text-xs">{r.path}</span>
                      <span className="tabular-nums text-ink/70">{r.hits}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h2 className="font-serif text-xl mb-3">AI'dan gelinen sayfalar</h2>
              {data.topAiRefPages.length === 0 ? (
                <p className="text-sm text-ink/60">—</p>
              ) : (
                <ul className="text-sm space-y-1">
                  {data.topAiRefPages.map((r) => (
                    <li key={r.path} className="flex justify-between gap-3">
                      <span className="truncate font-mono text-xs">{r.path}</span>
                      <span className="tabular-nums text-ink/70">{r.visits}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Recent activity */}
          <section>
            <h2 className="font-serif text-xl mb-3">Son bot ziyaretleri</h2>
            {data.recentBotHits.length === 0 ? (
              <p className="text-sm text-ink/60">—</p>
            ) : (
              <ul className="text-xs space-y-1 font-mono">
                {data.recentBotHits.map((r, i) => (
                  <li key={i} className="flex gap-3 items-baseline">
                    <span className="text-ink/50 shrink-0">{fmt(r.created_at)}</span>
                    <span className="text-moss shrink-0">{r.bot_name}</span>
                    <span className="truncate text-ink/80">{r.path}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="text-xs text-ink/50 border-t border-ink/10 pt-4">
            Not: Bot ziyaretleri sunucuya isabet eden HTML sayfa isteklerinden UA
            eşleşmesiyle tespit edilir. AI referrer sinyali yalnızca kullanıcı bir
            AI arayüzünden linke tıkladığında ve tarayıcı referrer'ı yolladığında
            gelir — Claude ve Gemini çoğunlukla bunu göndermez.
          </p>
        </div>
      )}
    </div>
  );
}
